/**
 * uploads.ts — the ONE file-upload path: presigned PUT straight to OUR MinIO.
 *
 *   1. ask the backend for a presigned PUT URL   (POST /storage/upload-url)
 *   2. PUT the bytes straight to MinIO            (the browser, not the API)
 *   3. return the storage KEY — that is what a row stores
 *
 * ── MINIO ONLY, AND A FAILURE IS A THROWN ERROR (2026-10-17) ───────────────────
 * This used to be a "dual path": on any MinIO failure it fell back to a
 * Supabase Storage callback. On this branch there is no Supabase Storage — the
 * `supabase.storage` object is a shim that answers every call with an
 * "unavailable" error — so the fallback could only ever fail, and the caller
 * got `null`. ChannelsPage then sent the message WITHOUT the file the person had
 * attached, and said nothing. The fallback is gone. Any failure — no backend
 * configured, presign refused, the PUT refused, the network — throws an Error
 * whose message is fit to show, and nothing reports success unless MinIO
 * answered the PUT with a 2xx.
 *
 * ── WHY A KEY AND NOT A URL ────────────────────────────────────────────────────
 * It used to also mint a one-hour download URL and hand THAT back to be stored
 * on the row. A presigned URL is a short-lived capability: stored, it is a dead
 * link an hour later. The key is the identifier; the URL is minted at render
 * (`useSignedUrl` in storage.ts → GET /storage/download-url).
 *
 * ── KEYS ───────────────────────────────────────────────────────────────────────
 * `<domain>/<workspace_id>/<rest>` — see `storageKey()`. The backend refuses
 * (403) any key whose workspace segment is not the caller's own, on upload,
 * download and delete alike.
 */

import { api, apiConfigured, requireWorkspaceId } from './apiClient';

/** Mirrors `_ALLOWED_DOMAINS` in backend/app/api/v1/storage.py. */
export type UploadDomain =
  | 'channels'
  | 'dms'
  | 'avatars'
  | 'tickets'
  | 'documents'
  | 'tasks'
  // 2026-10-20: files in a record's 'file' custom field (deals, leads, …) — fileFields.ts.
  | 'fields';

/** What a successful upload stored. `key` is the value to persist on the row. */
export interface StoredFile {
  key: string;
  name: string;
  size: number;
  type: string;
}

interface PresignResponse {
  url: string;
  method: string;
  headers: Record<string, string>;
  key: string;
  bucket: string;
  expires_in: number;
}

/**
 * The key for a new object: `<domain>/<the signed-in person's workspace>/<segments…>`.
 * Throws (via `requireWorkspaceId`) if nobody with a workspace is signed in —
 * never a key without one.
 */
export function storageKey(domain: UploadDomain, ...segments: string[]): string {
  return [domain, requireWorkspaceId(), ...segments].join('/');
}

/** `<epoch-ms>-<8 random chars>.<ext>` — collision-proof and safe for the key charset. */
export function uniqueFileName(file: File): string {
  const m = /\.([A-Za-z0-9]{1,10})$/.exec(file.name);
  const rand = Math.random().toString(36).slice(2, 10);
  return `${Date.now()}-${rand}${m ? `.${m[1].toLowerCase()}` : ''}`;
}

/** End a server's reason with exactly one full stop, whether or not it brought one. */
function sentence(reason: string): string {
  const r = reason.trim();
  return /[.!?]$/.test(r) ? r : `${r}.`;
}

/**
 * Upload one file to MinIO under `key`. Resolves ONLY once MinIO has accepted the
 * bytes; every other outcome throws an Error with a message fit to show.
 */
export async function uploadFile(
  file: File,
  opts: { key: string; expiresIn?: number },
): Promise<StoredFile> {
  const name = file.name || 'file';
  if (!apiConfigured()) {
    throw new Error(
      `Couldn’t upload “${name}”: file storage isn’t set up (no backend address is configured), so nothing was stored.`,
    );
  }

  const presigned = await api.post<PresignResponse>('/storage/upload-url', {
    key: opts.key,
    filename: name,
    content_type: file.type || 'application/octet-stream',
    size_bytes: file.size,
    expires_in: opts.expiresIn ?? 900,
  });
  if (!presigned.ok) {
    throw new Error(`Couldn’t upload “${name}”: ${sentence(presigned.error || `the server refused it (${presigned.status})`)}`);
  }
  if (!presigned.data || typeof presigned.data.url !== 'string' || typeof presigned.data.key !== 'string') {
    throw new Error(`Couldn’t upload “${name}”: the server did not return an upload address.`);
  }

  let put: Response;
  try {
    // Content-Type MUST match what was signed, or MinIO answers SignatureDoesNotMatch.
    put = await fetch(presigned.data.url, {
      method: 'PUT',
      headers: presigned.data.headers,
      body: file,
    });
  } catch (err) {
    throw new Error(
      `Couldn’t upload “${name}”: file storage could not be reached (${err instanceof Error ? err.message : 'network error'}).`,
    );
  }
  if (!put.ok) {
    throw new Error(`Couldn’t upload “${name}”: file storage refused it (${put.status}).`);
  }

  return {
    key: presigned.data.key,
    name,
    size: file.size,
    type: file.type || 'application/octet-stream',
  };
}

/**
 * Delete one object. Resolves only on the backend's 2xx; throws otherwise.
 * Deleting a key that is already gone is a success (S3 semantics).
 */
export async function deleteStoredFile(key: string): Promise<void> {
  if (!apiConfigured()) {
    throw new Error('Couldn’t delete the file: file storage isn’t set up (no backend address is configured).');
  }
  const res = await api.delete(`/storage/object?key=${encodeURIComponent(key)}`);
  if (!res.ok) {
    throw new Error(`Couldn’t delete the file from storage: ${sentence(res.error || `the server refused it (${res.status})`)}`);
  }
}
